<template>
  <el-row :gutter="15">
    <el-col :span="24">
      <el-card shadow="never">
        <div
          slot="header"
          class="clearfix"
        >
          <span>Track Consignments</span>
        </div>

        <el-form @submit.native.prevent="track">
          <el-form-item label="Consignment Numbers (up to 50)">
            <el-input
              ref="rawInput"
              v-model="rawInput"
              autofocus
              auto-complete="off"
              type="textarea"
              :rows="4"
              resize="none"
              placeholder="Enter one consignment per line (or comma separated)"
            />
          </el-form-item>

          <el-form-item class="mb-0">
            <el-button
              type="primary"
              :loading="isLoading"
              @click.native="track"
            >
              Track
            </el-button>
            <el-button @click.native="clearAll">
              Clear
            </el-button>
            <el-button
              :disabled="!results.length"
              @click.native="exportResults"
            >
              Export
            </el-button>
            <el-button
              :disabled="!hasProofOfDelivery"
              @click.native="bulkDownloadProofOfDelivery"
            >
              Download All PODs
            </el-button>
            <span class="text-muted ml-2">{{ parsedQueries.length }}/50</span>
          </el-form-item>
        </el-form>
      </el-card>
    </el-col>

    <el-col
      :span="24"
      class="mt-3"
    >
      <el-alert
        v-if="errors.length"
        type="warning"
        :closable="false"
        show-icon
      >
        <template slot="title">
          {{ errors.length }} consignments could not be loaded
        </template>
        <div
          v-for="error in errors"
          :key="error.query"
        >
          {{ error.query }}: {{ error.message }}
        </div>
      </el-alert>
    </el-col>

    <el-col
      :span="24"
      class="mt-3"
    >
      <el-card
        v-loading="isLoading"
        shadow="never"
      >
        <div
          slot="header"
          class="clearfix"
        >
          <span>Results</span>
          <span
            class="text-muted"
            style="float: right;"
          >{{ results.length }} loaded</span>
        </div>

        <div>
          <p
            v-if="!isLoading && !results.length"
            class="text-muted"
          >
            No results to display. Enter consignment numbers above and click "Track" to load tracking information.
          </p>
        </div>

        <div
          v-for="entry in results"
          :key="entry.query"
          class="result-block"
        >
          <el-row
            :gutter="10"
            class="mb-2"
          >
            <el-col :md="8">
              <strong>Consignment:</strong>
              <ConsignmentLink :consignment="entry.consignment">
                {{ entry.consignment.con_full || '-' }}
              </ConsignmentLink>
            </el-col>
            <el-col :md="8">
              <strong>Sender:</strong> {{ entry.consignment.con_sender_name || '-' }}
            </el-col>
            <el-col :md="8">
              <strong>Receiver:</strong> {{ entry.consignment.con_receiver_name || '-' }}
            </el-col>
          </el-row>

          <el-table
            :data="formatTrackings(entry.consignment.trackings)"
            size="mini"
            stripe
            border
          >
            <el-table-column
              label="Level"
              prop="level"
              width="180"
            />
            <el-table-column
              label="Event"
              prop="event"
            />
            <el-table-column
              label="Depot"
              prop="depot"
              width="140"
            />
            <el-table-column
              label="Scanned At"
              prop="scannedAt"
              width="170"
            />
            <el-table-column
              label="Created At"
              prop="createdAt"
              width="170"
            />
            <el-table-column
              label="POD"
              width="90"
              align="center"
            >
              <template slot-scope="scope">
                <el-button
                  v-if="scope.row.proofOfDeliveryId"
                  plain
                  type="primary"
                  icon="el-icon-download"
                  size="mini"
                  @click.native="$file.download(scope.row.proofOfDeliveryId)"
                />
                <span v-else>-</span>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-card>
    </el-col>
  </el-row>
</template>

<script>
import ConsignmentLink from '@/components/Popovers/ConsignmentLink'
import { SET_FILTERS, CLEAR_FILTERS } from '@/store/mutation-types'

export default {
  components: {
    ConsignmentLink
  },
  data () {
    return {
      title: 'Tracking',
      breadcrumb: {
        name: 'Tracking',
        route: { name: 'freight-tracking' }
      },
      rawInput: '',
      isLoading: false,
      results: [],
      errors: []
    }
  },
  computed: {
    parsedQueries () {
      return this.rawInput
        .split(/\n|,/)
        .map(value => value.trim())
        .filter(Boolean)
        .filter((value, index, arr) => arr.indexOf(value) === index)
    },
    hasProofOfDelivery () {
      return this.getProofOfDeliveryIds().length > 0
    },
    storeFilters () {
      return this.$store.getters['configuration/search/storeFilters']
    }
  },
  watch: {
    rawInput () {
      this.persistRememberedList()
    }
  },
  created () {
    this.restoreRememberedList()
  },
  activated () {
    this.restoreRememberedList()
    this.$nextTick(() => {
      if (this.$refs.rawInput) {
        this.$refs.rawInput.focus()
      }
    })
  },
  methods: {
    getRememberKey () {
      return `${this.$route.fullPath}::tracking-list`
    },
    restoreRememberedList () {
      if (!this.storeFilters) return

      const remembered = this.$store.getters['filter/filters'](this.getRememberKey())
      if (remembered && typeof remembered.rawInput === 'string') {
        this.rawInput = remembered.rawInput
      }
    },
    persistRememberedList () {
      if (!this.storeFilters) return

      this.$store.commit(`filter/${SET_FILTERS}`, {
        routePath: this.getRememberKey(),
        filters: {
          rawInput: this.rawInput
        }
      })
    },
    clearRememberedList () {
      this.$store.commit(`filter/${CLEAR_FILTERS}`, this.getRememberKey())
    },
    getConsignmentResource () {
      // Use plural if present, otherwise fall back to singular SDK resource name.
      return this.$sdk.consignments || this.$sdk.consignment
    },
    clearAll () {
      this.rawInput = ''
      this.results = []
      this.errors = []
      this.clearRememberedList()
    },
    csvEscape (value) {
      const safe = value == null ? '' : String(value)
      return `"${safe.replace(/"/g, '""')}"`
    },
    exportResults () {
      if (!this.results.length) {
        this.$message.warning('No results to export')
        return
      }

      const headers = [
        'query',
        'consignment',
        'sender',
        'receiver',
        'level',
        'event',
        'depot',
        'scanned_at',
        'created_at',
        'proof_of_delivery_id'
      ]

      const rows = []

      this.results.forEach(entry => {
        const base = {
          query: entry.query,
          consignment: this.$_.get(entry, 'consignment.con_full', '-'),
          sender: this.$_.get(entry, 'consignment.con_sender_name', '-'),
          receiver: this.$_.get(entry, 'consignment.con_receiver_name', '-')
        }

        const trackings = this.formatTrackings(this.$_.get(entry, 'consignment.trackings', []))

        trackings.forEach(t => {
          rows.push([
            base.query,
            base.consignment,
            base.sender,
            base.receiver,
            t.level,
            t.event,
            t.depot,
            t.scannedAt,
            t.createdAt,
            t.proofOfDeliveryId || ''
          ])
        })
      })

      const csv = [headers, ...rows]
        .map(row => row.map(this.csvEscape).join(','))
        .join('\n')

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.setAttribute('download', `tracking-export-${this.$moment().format('YYYYMMDD-HHmmss')}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)
    },
    getProofOfDeliveryIds () {
      const ids = []
      this.results.forEach(entry => {
        const trackings = this.$_.get(entry, 'consignment.trackings', [])
        trackings.forEach(row => {
          const podId = this.$_.get(row, 'scan.proof_of_delivery.id')
          if (podId) {
            ids.push(podId)
          }
        })
      })
      return this.$_.uniq(ids)
    },
    bulkDownloadProofOfDelivery () {
      const ids = this.getProofOfDeliveryIds()

      if (!ids.length) {
        this.$message.warning('No proof of delivery files available')
        return
      }

      ids.forEach((id, index) => {
        window.setTimeout(() => this.$file.download(id), index * 150)
      })

      this.$message.success(`Downloading ${ids.length} proof of delivery files`)
    },
    async track () {
      const queries = this.parsedQueries

      if (!queries.length) {
        this.$message.warning('Enter at least one consignment number')
        return
      }

      if (queries.length > 50) {
        this.$message.warning('You can track a maximum of 50 consignments at once')
        return
      }

      this.isLoading = true
      this.results = []
      this.errors = []

      try {
        const loaded = await Promise.all(queries.map(query => this.fetchConsignment(query)))

        loaded.forEach(entry => {
          if (entry.consignment) {
            this.results.push(entry)
          } else {
            this.errors.push({ query: entry.query, message: entry.message || 'Not found' })
          }
        })

        if (!this.results.length) {
          this.$message.warning('No consignments found')
        }
      } finally {
        this.isLoading = false
      }
    },
    async fetchConsignment (query) {
      const resource = this.getConsignmentResource()

      try {
        const list = await resource.list(builder => {
          builder.filters = { consignment_con_full: query }
          builder.limit = 10
          builder.include = [
            'carrier',
            'service',
            'trackings.scan',
            'trackings.level',
            'trackings.level.type'
          ]
          builder.order = 'created_at:desc'
        })

        const found = list.find(c => String(c.con_full).toLowerCase() === query.toLowerCase()) || list[0]

        if (!found) {
          return { query, consignment: null, message: 'No matching consignment' }
        }

        const consignment = await resource.read(found.id, request => {
          request.include = [
            'carrier',
            'service',
            'trackings.scan.proof_of_delivery',
            'trackings.level',
            'trackings.level.type'
          ]
        })

        return { query, consignment }
      } catch {
        return { query, consignment: null, message: 'Failed to load consignment' }
      }
    },
    formatTrackings (trackings = []) {
      if (!Array.isArray(trackings) || !trackings.length) {
        return [
          {
            level: '-',
            event: 'No tracking events',
            depot: '-',
            scannedAt: '-',
            createdAt: '-'
          }
        ]
      }

      return this.$_.clone(trackings)
        .reverse()
        .map(row => ({
          level: this.$_.get(row, 'level.name', '-'),
          event: this.$_.get(row, 'scan.text', '-'),
          depot: this.$_.get(row, 'scan.depot', '-'),
          scannedAt: this.$_.get(row, 'scan.scanned_at') ? this.$moment(this.$_.get(row, 'scan.scanned_at')).format('DD/MM/YYYY hh:mm a') : '-',
          createdAt: row.created_at ? this.$moment(row.created_at).format('DD/MM/YYYY hh:mm a') : '-',
          proofOfDeliveryId: this.$_.get(row, 'scan.proof_of_delivery.id', null)
        }))
    }
  }
}
</script>

<style scoped>
.result-block {
  margin-bottom: 18px;
}

.result-block:last-child {
  margin-bottom: 0;
}

.el-form-item {
  margin-bottom: 22px;
}
</style>
